/**
 * Cross-platform tracking-consent gate (GK-520).
 *
 * One switch, several ways of deciding it. Whatever the platform, the decision ends up in
 * Shopify's Customer Privacy API, which is what the Shopify app pixels honour - each of them
 * collects only while marketing/analytics consent is granted.
 *
 * Where the decision comes from:
 *   - iOS native: Apple requires an App Tracking Transparency prompt (guideline 5.1.2(i)),
 *     so the ATT status is authoritative and the cookie banner stays hidden.
 *   - Android native and web: Android has no OS-level ATT equivalent, so the decision comes
 *     from the cookie banner in component-modal.js, which writes consent itself.
 *
 * Loaded without `defer` on purpose: a decision has to be in place before the pixels
 * injected by content_for_header start collecting, otherwise they collect first and ask later.
 */
(function () {
  'use strict';

  var ATT_CACHE_KEY = 'gk-att-status';

  /** Writes a consent decision to Shopify's Customer Privacy API. */
  class ShopifyConsentGateway {
    apply(granted) {
      var shopify = window.Shopify;
      if (!shopify || !shopify.loadFeatures) return;

      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
        if (error) return;
        shopify.customerPrivacy.setTrackingConsent(
          {
            analytics: granted,
            marketing: granted,
            // Klaviyo and Blockify both declare their collection as a data sale, so this
            // is the flag that actually stops those two.
            sale_of_data: granted,
            // Functionality rather than tracking, so it is not gated on the decision.
            preferences: true,
          },
          function () {}
        );
      });
    }
  }

  /**
   * A platform's way of obtaining the user's tracking decision.
   *
   * Subclasses answer three things: whether they govern the current platform, what the
   * user last decided, and how to ask. Add a platform by adding a subclass and registering
   * it with the controller below - nothing else needs to change.
   */
  class TrackingConsentSource {
    /** True when this source governs the current platform. */
    get isActive() {
      return false;
    }

    /** True when something else already writes consent, so the controller stays out of it. */
    get delegatesToBanner() {
      return false;
    }

    /** Last known decision, or null if the user has never answered. */
    readCachedDecision() {
      return null;
    }

    /** Resolves the decision, prompting if needed. Resolves null when unanswerable. */
    requestDecision() {
      return Promise.resolve(null);
    }
  }

  /** iOS native: decision comes from Apple's App Tracking Transparency prompt. */
  class AppTrackingTransparencySource extends TrackingConsentSource {
    constructor(capacitor) {
      super();
      this.capacitor = capacitor;
    }

    get plugin() {
      return (this.capacitor && this.capacitor.Plugins && this.capacitor.Plugins.AppTracking) || null;
    }

    get isActive() {
      if (!this.capacitor || !this.capacitor.isNativePlatform || !this.capacitor.isNativePlatform()) return false;
      if (this.capacitor.getPlatform && this.capacitor.getPlatform() !== 'ios') return false;
      // App builds predating the ATT plugin cannot be asked, so let the banner handle them.
      return Boolean(this.plugin && this.plugin.getStatus);
    }

    readCachedDecision() {
      try {
        var status = localStorage.getItem(ATT_CACHE_KEY);
        return status ? status === 'authorized' : null;
      } catch (e) {
        return null;
      }
    }

    requestDecision() {
      var plugin = this.plugin;
      return plugin
        .getStatus()
        .then(function (result) {
          return result.status === 'notDetermined' ? plugin.requestPermission() : result;
        })
        .then(function (result) {
          try {
            localStorage.setItem(ATT_CACHE_KEY, result.status);
          } catch (e) {}
          return result.status === 'authorized';
        });
    }
  }

  /**
   * Android native and web: the cookie banner collects the decision and writes consent
   * itself, so this source only claims the platform and steps aside. Terminal fallback,
   * hence isActive is unconditionally true - keep it last in the controller's list.
   */
  class BannerConsentSource extends TrackingConsentSource {
    get isActive() {
      return true;
    }

    get delegatesToBanner() {
      return true;
    }
  }

  /** Picks the first source that governs this platform and applies its decision. */
  class TrackingConsentController {
    constructor(gateway, sources) {
      this.gateway = gateway;
      this.sources = sources;
    }

    start() {
      var source = null;
      for (var i = 0; i < this.sources.length; i++) {
        if (this.sources[i].isActive) {
          source = this.sources[i];
          break;
        }
      }
      if (!source) return;

      // Single source of truth for "the prompt is asking, so the banner must not".
      // component-modal.js reads the flag; native-app-hide.liquid keys its CSS off the class.
      // Set from <head> before the body renders, so the banner never flashes.
      var handledByPrompt = !source.delegatesToBanner;
      window.GKTrackingConsent = { handledByPrompt: handledByPrompt };
      if (handledByPrompt) document.documentElement.classList.add('consent-via-prompt');

      if (source.delegatesToBanner) return;

      // Withhold consent until the prompt is answered so nothing collects in the gap.
      var cached = source.readCachedDecision();
      var gateway = this.gateway;
      gateway.apply(cached === true);

      source
        .requestDecision()
        .then(function (granted) {
          if (granted !== null && granted !== cached) gateway.apply(granted);
        })
        .catch(function () {});
    }
  }

  new TrackingConsentController(new ShopifyConsentGateway(), [
    new AppTrackingTransparencySource(window.Capacitor),
    new BannerConsentSource(),
  ]).start();
})();
